import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Play, RotateCw, Save, Search, X } from "lucide-react";
import { AddRowForm } from "./AddRowForm";
import { InkDayBar } from "./InkDayBar";
import { INK_HEX, columnsFor, payloadRow, rowFromNumbers } from "./numbersModel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/preschool`;

function NumberRow({ person, row, cols, onChange, onPage }) {
  const low = row.confidence === "low";
  const empty = cols.every((c) => !String(row.cells[c.id] || "").trim());
  return (
    <tr className={`border-b border-slate-800/70 ${low ? "bg-amber-950/25" : ""}`} data-testid={`numbers-row-${person.idx}`}>
      <td className="py-1.5 pl-3 pr-2 text-[12px] text-slate-100 whitespace-nowrap">
        {low && <AlertTriangle className="inline h-3 w-3 text-amber-400 mr-1" />}
        {person.first} {person.last}
        {person.added && <span className="ml-1.5 text-[9px] uppercase tracking-widest text-emerald-400" data-testid={`numbers-added-badge-${person.idx}`}>added</span>}
        {empty && <span className="ml-2 text-[10px] text-slate-500">no numbers</span>}
        {row.page && (
          <button type="button" onClick={() => onPage(row.page)} className="ml-2 text-[10px] font-mono text-cyan-400 hover:text-cyan-200"
            data-testid={`numbers-page-link-${person.idx}`} title="Show this page of the sheet">p.{row.page}</button>
        )}
      </td>
      <td className="px-2 text-[11px] text-slate-500 whitespace-nowrap">{person.is_staff ? "Staff" : person.room}</td>
      {cols.map((c) => (
        <td key={c.id} className="px-1">
          <input
            value={row.cells[c.id] || ""}
            onChange={(e) => onChange(person.idx, c.id, e.target.value)}
            className="w-20 h-7 px-2 rounded-sm bg-slate-950 border border-slate-800 text-[12px] font-mono focus:outline-none focus:border-amber-400"
            style={{ color: c.ink ? INK_HEX[c.ink] || "#f1f5f9" : "#f1f5f9" }}
            data-testid={`numbers-input-${person.idx}-${c.k === "x" ? `x${c.col}` : c.k}${c.ink ? `-${c.ink}` : ""}`}
          />
        </td>
      ))}
    </tr>
  );
}

export function NumbersReviewDialog({ task, onClose, onConfirmed }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState({});
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [cols, setCols] = useState([]);
  const [inkDays, setInkDays] = useState({});
  const [pause, setPause] = useState(true);
  const [colKinds, setColKinds] = useState({});

  useEffect(() => {
    axios.get(`${API}/tasks/${task.task_id}`).then(({ data: d }) => {
      setData(d);
      const c = columnsFor(d.numbers);
      setCols(c);
      setColKinds(d.col_kinds || {});
      const known = new Set((d.days || []).map((x) => x.day));
      setInkDays(Object.fromEntries(Object.entries(d.ink_days || {}).filter(([, v]) => known.has(v))));
      const m = {};
      d.numbers.forEach((n) => { m[n.idx] = rowFromNumbers(n, c); });
      d.roster.forEach((p) => { m[p.idx] = m[p.idx] || rowFromNumbers({ idx: p.idx, confidence: "low" }, c); });
      setRows(m);
    }).catch((e) => toast.error(e.response?.data?.detail || "Couldn't load task"));
  }, [task.task_id]);

  // Same order as the scanned sheet (page, then row position) so it can be checked line by line.
  const people = useMemo(() => {
    const pos = Object.fromEntries((data?.numbers || []).map((n) => [n.idx, n]));
    const key = (p) => [p.added ? 1 : 0, pos[p.idx]?.page ?? 1e9, pos[p.idx]?.order ?? 1e9, p.idx];
    const all = [...(data?.roster || [])].sort((a, b) => {
      const ka = key(a), kb = key(b);
      for (let i = 0; i < ka.length; i++) if (ka[i] !== kb[i]) return ka[i] - kb[i];
      return 0;
    });
    const s = q.trim().toLowerCase();
    return s ? all.filter((p) => `${p.first} ${p.last} ${p.room}`.toLowerCase().includes(s)) : all;
  }, [data, q]);
  const rooms = useMemo(() => {
    return [...new Set([...(data?.roster || []).map((p) => p.room).filter(Boolean), "Staff"])];
  }, [data]);

  const reread = async () => {
    if (!window.confirm("Read the sheet again? Numbers typed in this table will be replaced (added rows are kept).")) return;
    try {
      await axios.post(`${API}/tasks/${task.task_id}/reread`);
      toast.success("Re-reading the sheet — the task will show Review when done");
      onConfirmed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't re-read");
    }
  };

  const addRow = async (body) => {
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/tasks/${task.task_id}/roster`, body);
      setData((d) => ({ ...d, roster: [...d.roster, r.person], numbers: [...d.numbers, r.row] }));
      setRows((prev) => ({ ...prev, [r.person.idx]: rowFromNumbers(r.row, cols) }));
      toast.success(`Added ${body.first} ${body.last} — enter their numbers below`, { position: "top-center" });
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add row");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onChange = (idx, id, v) => setRows((prev) => ({ ...prev, [idx]: { ...prev[idx], cells: { ...prev[idx].cells, [id]: v } } }));
  const payload = () => Object.values(rows).map((r) => payloadRow(r, cols));
  const lowCount = Object.values(rows).filter((r) => r.confidence === "low").length;

  const save = async (confirm) => {
    setBusy(true);
    try {
      if (confirm) {
        const { data: r } = await axios.post(`${API}/tasks/${task.task_id}/confirm`, { rows: payload(), ink_days: inkDays, col_kinds: colKinds, pause_after_match: pause });
        toast.success(r.waiting_for_photos
          ? "Numbers confirmed — matching starts automatically when photos finish uploading"
          : "Numbers confirmed — matching photos in the background");
        onConfirmed();
      } else {
        await axios.put(`${API}/tasks/${task.task_id}/numbers`, { rows: payload(), ink_days: inkDays, col_kinds: colKinds });
        toast.success("Numbers saved", { position: "top-center" });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const inkMissing = (data?.days || []).length > 1 && (data?.inks || []).some((i) => !data.days.some((d) => d.day === inkDays[i]));
  const colUnset = cols.some((c) => c.k === "x" && !colKinds[c.col]
    && Object.values(rows).some((r) => String(r.cells[c.id] || "").trim()));
  const isPdf = (data?.sheet_filename || "").toLowerCase().endsWith(".pdf");
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="numbers-review-dialog">
      <div className="w-[96vw] h-[92vh] bg-slate-950 border border-slate-800 rounded-md flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 h-14 border-b border-slate-800 shrink-0">
          <div>
            <h2 className="text-sm font-bold text-slate-100">Check the numbers · {task.name}</h2>
            <p className="text-[11px] text-slate-500">
              Compare with the scan, fix anything misread, then confirm.{colUnset && <span className="text-red-300" data-testid="numbers-colkind-warning"> Pick Sibling or Graduation for the column(s) without a header.</span>}{lowCount > 0 && <span className="text-amber-300"> {lowCount} row(s) flagged as hard to read.</span>}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" disabled={busy} onClick={reread} data-testid="numbers-reread-btn" title="Read the sheet again (replaces this table)"
              className="h-8 px-3 inline-flex items-center gap-1.5 rounded-sm border border-slate-700 text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:bg-slate-800 disabled:opacity-50">
              <RotateCw className="h-3.5 w-3.5" />Re-read sheet
            </button>
            <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-100" data-testid="numbers-review-close"><X className="h-5 w-5" /></button>
          </div>
        </div>
        {!data ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div>
        ) : (
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 min-h-0">
            <div className="border-r border-slate-800 bg-slate-900 min-h-0" data-testid="numbers-sheet-viewer">
              {isPdf
                ? <iframe key={page} title="sheet" src={`${data.sheet_url}#page=${page}`} className="w-full h-full" />
                : <img src={data.sheet_url} alt="sheet" className="w-full h-full object-contain" />}
            </div>
            <div className="flex flex-col min-h-0">
              <div className="px-4 py-2 border-b border-slate-800 flex items-center gap-2">
                <Search className="h-3.5 w-3.5 text-slate-500" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter names or rooms"
                  className="flex-1 h-8 bg-transparent text-[12px] text-slate-100 focus:outline-none" data-testid="numbers-filter-input" />
              </div>
              <InkDayBar inks={data.inks || []} days={data.days || []} inkDays={inkDays} autoDays={data.ink_days_auto || {}}
                onChange={(ink, day) => setInkDays((prev) => ({ ...prev, [ink]: day }))} />
              <div className="flex-1 overflow-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-slate-950">
                    <tr className="text-[10px] uppercase tracking-widest text-slate-500 text-left">
                      <th className="py-2 pl-3">Name</th><th className="px-2">Room</th>
                      {cols.map((c) => (
                        <th key={c.id} className="px-1 whitespace-nowrap" data-testid={`numbers-col-${c.k === "x" ? `x${c.col}` : c.k}-${c.ink || "plain"}`}>
                          {c.k === "x" ? (
                            <select value={colKinds[c.col] || ""} onChange={(e) => setColKinds((p) => ({ ...p, [c.col]: e.target.value }))}
                              className={`h-6 px-1 rounded-sm bg-slate-950 border text-[10px] normal-case tracking-normal text-slate-100 ${colKinds[c.col] ? "border-slate-700" : "border-red-500"}`}
                              data-testid={`numbers-colkind-select-${c.col}-${c.ink || "plain"}`} title="This column has no header on the sheet">
                              <option value="">No header — pick…</option>
                              <option value="sib">Sibling</option>
                              <option value="grad">Graduation</option>
                            </select>
                          ) : c.label}
                          {c.ink && <span className="ml-1 inline-flex items-center gap-1 normal-case tracking-normal" style={{ color: INK_HEX[c.ink] }}>
                            <span className="h-2 w-2 rounded-full" style={{ background: INK_HEX[c.ink] }} />{c.ink}
                          </span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {people.map((p) => rows[p.idx] && <NumberRow key={p.idx} person={p} row={rows[p.idx]} cols={cols} onChange={onChange} onPage={setPage} />)}
                  </tbody>
                </table>
                <AddRowForm rooms={rooms} onAdd={addRow} busy={busy} />
              </div>
              <div className="px-4 h-14 border-t border-slate-800 flex items-center justify-end gap-2 shrink-0">
                <label className="mr-auto inline-flex items-center gap-2 text-[11px] text-slate-300 cursor-pointer" data-testid="numbers-pause-toggle-label">
                  <input type="checkbox" checked={pause} onChange={(e) => setPause(e.target.checked)} className="accent-amber-400" data-testid="numbers-pause-toggle" />
                  Pause after matching so I can review before the ZIP
                </label>
                <button type="button" disabled={busy} onClick={() => save(false)} data-testid="numbers-save-btn"
                  className="h-9 px-4 inline-flex items-center gap-1.5 rounded-sm border border-slate-700 text-[11px] font-bold uppercase tracking-widest text-slate-300 hover:bg-slate-800 disabled:opacity-50">
                  <Save className="h-3.5 w-3.5" />Save draft
                </button>
                <button type="button" disabled={busy || inkMissing || colUnset} onClick={() => save(true)} data-testid="numbers-confirm-btn"
                  title={colUnset ? "Choose Sibling or Graduation for each column without a header" : inkMissing ? "Pick a day folder for each pen colour first" : ""}
                  className="h-9 px-4 inline-flex items-center gap-1.5 rounded-sm bg-amber-400 text-slate-950 text-[11px] font-bold uppercase tracking-widest hover:bg-amber-300 disabled:opacity-50">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}Confirm &amp; match photos
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
